import { useEffect, useState } from "react";

import { ActionBar } from "../../components/ActionBar";
import { Chip, ChipRow } from "../../components/Chip";
// the sheet chips wrap as the cycle log's do
import "../../components/CycleSheet.css";
import { Field } from "../../components/Fields";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Sheet } from "../../components/Sheet";
import { dayLabel, todayStr } from "../../domain/date";
import type { Contraception } from "../../domain/types";
import { useKaya } from "../../store/kayaStore";
import { setContraception } from "../../store/toggles";
import { type Form, useProfileForm } from "./profileForm";
import SettingsPage from "./SettingsPage";

const METHODS: { value: Contraception; label: string }[] = [
  { value: "none", label: "None" },
  { value: "pill", label: "Pill" },
  { value: "iud", label: "Hormonal IUD" },
  { value: "implant", label: "Implant" },
  { value: "injection", label: "Injection" },
  { value: "other", label: "Other" },
];

type Open = "cycleLen" | "periodLen" | "lastStart" | "method" | null;

/**
 * You › Cycle: the switch, the numbers predictions start from, and hormonal contraception
 * (ADR-033). Every change is kept as it is made, so the page has no Save bar: a switch or a
 * sheet's Save writes the form through `profileForm` (the one path the other settings screens
 * share), and contraception, which the form does not carry, goes straight to `setContraception`.
 * The logged periods themselves are data, not a setting, so they live on Progress › Cycle.
 */
export default function CycleSettings() {
  const { f, set, save } = useProfileForm();
  const contraception = useKaya((s) => s.S.cycle.contraception ?? "none");
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const [open, setOpen] = useState<Open>(null);
  const [saving, setSaving] = useState(false);

  // `save` reads the form as of its render, so a change is saved on the render after `set`
  // lands rather than in the same handler, where it would still see the old value.
  useEffect(() => {
    if (!saving) return;
    setSaving(false);
    void save();
  }, [saving, save]);

  const change = <K extends keyof Form>(k: K, v: Form[K]) => {
    set(k, v);
    setSaving(true);
  };

  const method = METHODS.find((m) => m.value === contraception) ?? METHODS[0];
  const chooseMethod = (v: Contraception) => {
    update((d) => setContraception(d, v));
    setOpen(null);
    showToast("Saved");
  };

  const numberSheet = (key: "cycleLen" | "periodLen", title: string, min: string, max: string, hint: string) => (
    <NumberSheet
      title={title}
      initial={f[key]}
      min={min}
      max={max}
      hint={hint}
      onClose={() => setOpen(null)}
      onSave={(v) => {
        change(key, v);
        setOpen(null);
      }}
    />
  );

  return (
    <SettingsPage title="Cycle">
      <RowGroup heading="Tracking">
        {/* The state lives with the other body facts; this page only points there (ADR-039). */}
        {f.periodsStopped ? (
          <NavRow
            title="Cycle tracking"
            subtitle="Off — periods have stopped. Change it under Body & goals."
            to="/you/body"
          />
        ) : (
          <NavRow
            kind="toggle"
            title="Cycle tracking"
            subtitle={f.cycleOn ? undefined : "Predicts period days and swaps in a gentle session on them"}
            checked={f.cycleOn}
            onChange={(v) => change("cycleOn", v)}
          />
        )}
        {f.cycleOn && !f.periodsStopped ? (
          <>
            <NavRow
              title="Average cycle length"
              value={`${f.cycleLen} days`}
              onClick={() => setOpen("cycleLen")}
            />
            <NavRow title="Period length" value={`${f.periodLen} days`} onClick={() => setOpen("periodLen")} />
            <NavRow
              title="Last period start"
              value={f.lastStart ? dayLabel(f.lastStart) : "–"}
              onClick={() => setOpen("lastStart")}
            />
            <NavRow
              kind="toggle"
              title="Irregular cycles"
              subtitle="Adds cycle care to Train; Kaya also spots it once three periods are logged"
              checked={f.irregular}
              onChange={(v) => change("irregular", v)}
            />
          </>
        ) : null}
      </RowGroup>

      {f.cycleOn ? (
        <>
          <RowGroup heading="Contraception">
            <NavRow
              title="Hormonal contraception"
              subtitle="pill, hormonal IUD, implant, injection, other"
              value={method.label}
              onClick={() => setOpen("method")}
            />
          </RowGroup>
          <Notice tone="info">
            On a hormonal method the bleed is not a natural cycle, so Kaya hides the phase and ovulation estimates
            and keeps the rest.
          </Notice>

          <RowGroup heading="Sessions">
            <NavRow
              kind="value"
              title="Cycle care session"
              subtitle="A late or irregular cycle adds a 15-min cycle care session to Train."
              value="Automatic"
            />
          </RowGroup>

          <RowGroup heading="Privacy">
            <NavRow
              kind="value"
              title="Symptoms never appear in badges. Cycle dates sync with your account like everything else."
            />
          </RowGroup>
        </>
      ) : null}

      {open === "cycleLen"
        ? numberSheet("cycleLen", "Average cycle length", "20", "45", "First day of one period to the first day of the next. Kaya refines it as you log.")
        : null}
      {open === "periodLen" ? numberSheet("periodLen", "Period length", "2", "8", "How many days a period usually lasts.") : null}
      {open === "lastStart" ? (
        <DateSheet
          initial={f.lastStart}
          onClose={() => setOpen(null)}
          onSave={(v) => {
            change("lastStart", v);
            setOpen(null);
          }}
        />
      ) : null}
      {open === "method" ? (
        <Sheet title="Hormonal contraception" sub="Only whether a method is hormonal changes what Kaya shows." onClose={() => setOpen(null)}>
          <div className="cs">
            <ChipRow ariaLabel="Hormonal contraception">
              {METHODS.map((m) => (
                <Chip key={m.value} selected={m.value === contraception} onClick={() => chooseMethod(m.value)}>
                  {m.label}
                </Chip>
              ))}
            </ChipRow>
          </div>
        </Sheet>
      ) : null}
    </SettingsPage>
  );
}

function NumberSheet({
  title,
  initial,
  min,
  max,
  hint,
  onClose,
  onSave,
}: {
  title: string;
  initial: string;
  min: string;
  max: string;
  hint: string;
  onClose: () => void;
  onSave: (v: string) => void;
}) {
  const [v, setV] = useState(initial);
  const n = Number(v);
  const ok = Number.isFinite(n) && n >= Number(min) && n <= Number(max);
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save", onClick: () => onSave(String(Math.round(n))), disabled: !ok, reason: `${min}–${max} days` }}
        />
      }
    >
      <Field label={title} unit="days" type="number" min={min} max={max} value={v} onChange={setV} hint={hint} invalid={!ok} />
    </Sheet>
  );
}

function DateSheet({ initial, onClose, onSave }: { initial: string; onClose: () => void; onSave: (v: string) => void }) {
  const [v, setV] = useState(initial);
  const ok = /^\d{4}-\d{2}-\d{2}$/.test(v) && v <= todayStr();
  return (
    <Sheet
      title="Last period start"
      sub="Logged as a period start; Progress › Cycle lists them all."
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save", onClick: () => onSave(v), disabled: !ok, reason: "Pick a day up to today" }}
        />
      }
    >
      <Field label="First day of the period" type="date" max={todayStr()} value={v} onChange={setV} />
    </Sheet>
  );
}
